import React, { useState } from "react";
import gallery4 from "../assets/images/NSS/New folder/gallery-4.webp";
import promptClassFlier from "../assets/images/PromptClass.jpg";
import { EmptyState } from "../components/SkeletonUI";

/* ── Constants ────────────────────────────────────────────────── */
const CATEGORIES = [
  "All", "Cisco", "Microsoft", "Azure", "Red Hat",
  "Cybersecurity", "Cloud", "Programming", "Project Management",
];

const VENDOR_COLORS = {
  Cisco:                { bg: "#1ba0d7", text: "#fff" },
  Microsoft:            { bg: "#00a1f1", text: "#fff" },
  Azure:                { bg: "#0078d4", text: "#fff" },
  "Red Hat":            { bg: "#EE0000", text: "#fff" },
  Cybersecurity:        { bg: "#E21F26", text: "#fff" },
  Cloud:                { bg: "#607078", text: "#fff" },
  Programming:          { bg: "#3776AB", text: "#fff" },
  "Project Management": { bg: "#C6A75E", text: "#fff" },
};

const CERT_TRACKS = [
  {
    vendor: "Cisco",
    color: "#1ba0d7",
    icon: "bi-hdd-network",
    levels: ["CCT", "CCNA", "CCNP", "CCIE"],
  },
  {
    vendor: "Microsoft",
    color: "#00a1f1",
    icon: "bi-windows",
    levels: ["AZ-900 Fundamentals", "AZ-104 Administrator", "AZ-305 Expert", "Specialty"],
  },
  {
    vendor: "AWS",
    color: "#FF9900",
    icon: "bi-cloud",
    levels: ["Cloud Practitioner", "Associate", "Professional", "Specialty"],
  },
  {
    vendor: "Cybersecurity",
    color: "#E21F26",
    icon: "bi-shield-check",
    levels: ["IT Fundamentals+", "A+ / Network+", "Security+", "CASP+ / CISSP"],
  },
];

const VENDOR_PARTNERS = [
  { name: "Cisco",               color: "#1ba0d7", img: "/Cisco.png" },
  { name: "Microsoft",           color: "#00a1f1", img: "/Microsoft.png" },
  { name: "Amazon Web Services", color: "#FF9900", img: "/aws.png" },
  { name: "CompTIA",             color: "#E21F26", img: "/comptia.png" },
  { name: "ISC²",                color: "#003087", img: "/ISC2.png" },
  { name: "VMware",              color: "#607078", img: "/vmware.png" },
];
const CAROUSEL_PARTNERS = [...VENDOR_PARTNERS, ...VENDOR_PARTNERS];

/* ── Training Data ────────────────────────────────────────────── */
const STATIC_TRAININGS = [
  // June 2026
  {
    id: 23,
    attributes: {
      title: "Cisco Enterprise Network Core Technologies",
      vendor: "Cisco",
      deliveryMode: "Instructor-led",
      description: "Core enterprise networking technologies for the ENCOR exam including dual-stack architecture, virtualisation, infrastructure, network assurance, and security.",
      duration: "5 days",
      nextDate: "2026-06-01",
    },
  },
  {
    id: 24,
    attributes: {
      title: "Active Directory Deep Dive (Installation & Configuration)",
      vendor: "Microsoft",
      deliveryMode: "Instructor-led",
      description: "Comprehensive Active Directory deployment, domain and forest configuration, group policy management, replication, and organisational unit design.",
      duration: "5 days",
      nextDate: "2026-06-01",
    },
  },
  {
    id: 25,
    attributes: {
      title: "Securing Active Directory Deep Dive",
      vendor: "Cybersecurity",
      deliveryMode: "Instructor-led",
      description: "Comprehensive training on securing Active Directory environments against modern threats, privilege escalation, lateral movement, and misconfigurations.",
      duration: "5 days",
      nextDate: "2026-06-08",
    },
  },
  {
    id: 26,
    attributes: {
      title: "Securing Active Directory Deep Dive Level 2",
      vendor: "Cybersecurity",
      deliveryMode: "Instructor-led",
      description: "Advanced Active Directory security covering BloodHound analysis, Kerberos attack chains, Golden Ticket attacks, and advanced environment hardening.",
      duration: "3 days",
      nextDate: "2026-06-15",
    },
  },
  {
    id: 27,
    attributes: {
      title: "Building a Data Warehouse in SQL 2022",
      vendor: "Microsoft",
      deliveryMode: "Instructor-led",
      description: "Design and implement a modern data warehouse using SQL Server 2022, including ETL pipelines, star schema modelling, and reporting integration.",
      duration: "5 days",
      nextDate: "2026-06-22",
    },
  },
  {
    id: 28,
    attributes: {
      title: "Microsoft SQL Server Master Class",
      vendor: "Microsoft",
      deliveryMode: "Instructor-led",
      description: "Advanced SQL Server administration covering performance tuning, high availability, disaster recovery, security, and database development techniques.",
      duration: "5 days",
      nextDate: "2026-06-29",
    },
  },
  {
    id: 29,
    attributes: {
      title: "VMware vSphere: Install, Configure, Manage",
      vendor: "Cloud",
      deliveryMode: "Instructor-led",
      description: "Install, configure, and manage VMware vSphere environments including ESXi hosts, vCenter Server, virtual machines, and virtual networking.",
      duration: "5 days",
      nextDate: "2026-06-29",
    },
  },
];

/* ── Monthly Schedule Data (current & upcoming confirmed dates) ── */
const SCHEDULE_MONTHS = [
  {
    month: "June 2026",
    shortMonth: "JUN",
    courses: [
      { dates: "Jun 1–5",      title: "Cisco Enterprise Network Core Technologies" },
      { dates: "Jun 1–5",      title: "Active Directory Deep Dive (Installation & Configuration)" },
      { dates: "Jun 8–12",     title: "Securing Active Directory Deep Dive" },
      { dates: "Jun 15–17",    title: "Securing Active Directory Deep Dive Level 2" },
      { dates: "Jun 22–26",    title: "Building a Data Warehouse in SQL 2022" },
      { dates: "Jun 29–Jul 3", title: "Microsoft SQL Server Master Class" },
      { dates: "Jun 29–Jul 3", title: "VMware vSphere: Install, Configure, Manage" },
    ],
  },
];

/* ── Coming Soon Months (Jul–Oct, no specific dates yet) ─────── */
const COMING_SOON_MONTHS = [
  {
    month: "July 2026",
    shortMonth: "JUL",
    courses: [
      "OpenShift / Ansible Training and Certification",
      "Software Development",
      "IT Service Management",
      "ITIL v.4",
      "Prince 2 Foundation",
    ],
  },
  {
    month: "August 2026",
    shortMonth: "AUG",
    courses: [
      "Data Analytics with Excel (Advanced)",
      "Networking Trainings",
      "Automation & RPA Fundamentals",
      "Cloud Security & Governance",
    ],
  },
  {
    month: "September 2026",
    shortMonth: "SEP",
    courses: [
      "Cybersecurity Awareness Training and Certification",
      "ISO Training and Certification",
      "Microsoft Azure Admin / Security",
      "Certified Ethical Hacker",
      "NetApp Ontap & Data Protection",
    ],
  },
  {
    month: "October 2026",
    shortMonth: "OCT",
    courses: [
      "DevOps Training",
      "Cloud Computing Training and Certifications",
      "Phishing Simulation & Security Best Practices",
      "IT Leadership and Support Training & Others",
    ],
  },
];


/* ── Vendor Badge ─────────────────────────────────────────────── */
function VendorBadge({ vendor }) {
  const colors = VENDOR_COLORS[vendor] || { bg: "#0a1a3a", text: "#fff" };
  return (
    <span
      style={{
        display: "inline-block",
        background: colors.bg,
        color: colors.text,
        fontSize: "0.68rem",
        fontWeight: 700,
        letterSpacing: "0.06em",
        padding: "3px 10px",
        borderRadius: 4,
        textTransform: "uppercase",
        flexShrink: 0,
      }}
    >
      {vendor}
    </span>
  );
}


/* ── Enquiry Modal ────────────────────────────────────────────── */
function EnquiryModal({ course, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  function handleChange(e) {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch("/.netlify/functions/send-inquiry", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          message: form.message,
          course: course?.attributes?.title || "General Training Enquiry",
        }),
      });
      if (!res.ok) throw new Error("Failed to send");
      setSuccess(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      onClick={e => e.target === e.currentTarget && onClose()}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.6)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        style={{
          background: "#fff",
          borderRadius: 16,
          padding: "40px 44px",
          width: "100%",
          maxWidth: 520,
          position: "relative",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: 16,
            right: 20,
            background: "none",
            border: "none",
            fontSize: "1.5rem",
            cursor: "pointer",
            color: "#9ca3af",
            lineHeight: 1,
          }}
        >
          ×
        </button>

        {success ? (
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                background: "#dcfce7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 20px",
              }}
            >
              <i className="bi bi-check-lg" style={{ fontSize: "1.8rem", color: "#16a34a" }} />
            </div>
            <h3 style={{ color: "#0a1a3a", fontWeight: 700, marginBottom: 8 }}>Enquiry received!</h3>
            <p style={{ color: "#6B7280", marginBottom: 24 }}>
              We'll get back to you within one business day.
            </p>
            <button className="btn btn-dark" onClick={onClose}>
              Close
            </button>
          </div>
        ) : (
          <>
            <p
              style={{
                fontSize: "0.72rem",
                color: "#009EDC",
                fontWeight: 700,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                marginBottom: 4,
              }}
            >
              Course Enquiry
            </p>
            <h3 style={{ color: "#0a1a3a", fontWeight: 700, marginBottom: 24, lineHeight: 1.3 }}>
              {course?.attributes?.title || "Training Enquiry"}
            </h3>

            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label className="form-label fw-semibold" style={{ fontSize: "0.85rem" }}>
                  Full name *
                </label>
                <input
                  className="form-control"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  placeholder="John Mensah"
                />
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold" style={{ fontSize: "0.85rem" }}>
                  Work email *
                </label>
                <input
                  type="email"
                  className="form-control"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  placeholder="john@company.com"
                />
              </div>

              <div className="mb-3">
                <label className="form-label fw-semibold" style={{ fontSize: "0.85rem" }}>
                  Phone number
                </label>
                <input
                  className="form-control"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="+233 XX XXX XXXX"
                />
              </div>

              <div className="mb-4">
                <label className="form-label fw-semibold" style={{ fontSize: "0.85rem" }}>
                  Message
                </label>
                <textarea
                  className="form-control"
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Tell us about your team's size and training goals..."
                />
              </div>

              {error && <p className="text-danger small mb-3">{error}</p>}

              <button
                type="submit"
                className="btn w-100"
                disabled={submitting}
                style={{
                  background: "#0a1a3a",
                  color: "#fff",
                  padding: "13px",
                  fontWeight: 600,
                  borderRadius: 8,
                  fontSize: "0.95rem",
                }}
              >
                {submitting ? "Sending…" : "Submit Enquiry"}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}


/* ── Training Card ────────────────────────────────────────────── */
function TrainingCard({ item, onEnquire }) {
  const attr = item.attributes;

  const formatDate = dateStr => {
    if (!dateStr) return null;
    try {
      return new Date(dateStr).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <article
      style={{
        background: "#fff",
        borderRadius: 14,
        overflow: "hidden",
        boxShadow: "0 4px 20px rgba(0,0,0,0.07)",
        display: "flex",
        flexDirection: "column",
        transition: "transform 0.25s ease, box-shadow 0.25s ease",
      }}
      onMouseEnter={e => {
        e.currentTarget.style.transform = "translateY(-5px)";
        e.currentTarget.style.boxShadow = "0 16px 40px rgba(0,0,0,0.12)";
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.07)";
      }}
    >
      {/* Coloured top accent bar */}
      <div
        style={{
          height: 5,
          // background: VENDOR_COLORS[attr?.vendor]?.bg || "#009EDC",
          flexShrink: 0,
        }}
      />

      <div style={{ padding: "20px 22px 22px", display: "flex", flexDirection: "column", flex: 1 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
          {attr?.vendor && <VendorBadge vendor={attr.vendor} />}
          {attr?.deliveryMode && (
            <span style={{ fontSize: "0.72rem", color: "#6B7280", fontWeight: 500 }}>
              {attr.deliveryMode}
            </span>
          )}
        </div>

        <h6
          style={{
            fontWeight: 700,
            color: "#0a1a3a",
            lineHeight: 1.45,
            marginBottom: 8,
            fontSize: "0.95rem",
          }}
        >
          {attr?.title}
        </h6>

        {attr?.description && (
          <p
            style={{
              fontSize: "0.83rem",
              color: "#6B7280",
              lineHeight: 1.65,
              marginBottom: 14,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {attr.description}
          </p>
        )}

        <div style={{ display: "flex", gap: 16, marginBottom: 18, flexWrap: "wrap" }}>
          {attr?.duration && (
            <span style={{ fontSize: "0.8rem", color: "#374151", display: "flex", alignItems: "center", gap: 5 }}>
              <i className="bi bi-clock" style={{ color: "#009EDC" }} />
              {attr.duration}
            </span>
          )}
          {attr?.nextDate && (
            <span style={{ fontSize: "0.8rem", color: "#374151", display: "flex", alignItems: "center", gap: 5 }}>
              <i className="bi bi-calendar3" style={{ color: "#009EDC" }} />
              {formatDate(attr.nextDate)}
            </span>
          )}
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: "auto" }}>
          <button
            className="btn btn-sm flex-fill"
            onClick={() => onEnquire(item)}
            style={{
              background: "#0a1a3a",
              color: "#fff",
              fontWeight: 600,
              fontSize: "0.82rem",
              borderRadius: 6,
              padding: "8px 0",
            }}
          >
            Enquire
          </button>
        </div>
      </div>
    </article>
  );
}


/* ── Schedule Section ─────────────────────────────────────────── */
function ScheduleSection({ onEnquire }) {
  const [activeMonth, setActiveMonth] = useState(0);

  return (
    <section style={{ background: "#fff", padding: "72px 0 80px" }}>
      <div className="container">

        {/* Header */}
        <p
          style={{
            fontSize: "0.72rem",
            color: "#009EDC",
            fontWeight: 700,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            marginBottom: 8,
          }}
        >
          H2 2026 Training Calendar
        </p>
        <h2 style={{ fontWeight: 700, color: "#0a1a3a", marginBottom: 10 }}>
          Upcoming training schedule
        </h2>
        <p
          style={{
            color: "#6B7280",
            maxWidth: 520,
            marginBottom: 44,
            lineHeight: 1.7,
          }}
        >
          Browse confirmed course dates for January through June 2026, with further
          programmes coming in Q3 and Q4.
        </p>

        {/* Month tabs — Jan to Jun */}
        <div
          style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            marginBottom: 32,
          }}
        >
          {SCHEDULE_MONTHS.map((m, i) => (
            <button
              key={m.shortMonth}
              onClick={() => setActiveMonth(i)}
              style={{
                padding: "8px 20px",
                borderRadius: 50,
                border: "2px solid",
                borderColor: activeMonth === i ? "#009EDC" : "#e5e7eb",
                background: activeMonth === i ? "#009EDC" : "#fff",
                color: activeMonth === i ? "#fff" : "#374151",
                fontWeight: 700,
                fontSize: "0.82rem",
                cursor: "pointer",
                transition: "all 0.2s ease",
                letterSpacing: "0.04em",
              }}
            >
              {m.shortMonth}
            </button>
          ))}
        </div>

        {/* Active month course list */}
        {(() => {
          const month = SCHEDULE_MONTHS[activeMonth];
          return (
            <div
              style={{
                background: "#f8f9fc",
                borderRadius: 16,
                padding: "28px 32px",
                marginBottom: 48,
              }}
            >
              <h4
                style={{
                  color: "#0a1a3a",
                  fontWeight: 700,
                  marginBottom: 20,
                  fontSize: "1.05rem",
                }}
              >
                {month.month}
              </h4>

              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {month.courses.map((c, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 16,
                      background: "#fff",
                      borderRadius: 10,
                      padding: "14px 18px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                    }}
                  >
                    {/* Date badge */}
                    <div
                      style={{
                        background: "#0a1a3a",
                        color: "#fff",
                        borderRadius: 8,
                        padding: "6px 12px",
                        fontSize: "0.73rem",
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                        minWidth: 100,
                        textAlign: "center",
                        letterSpacing: "0.02em",
                      }}
                    >
                      {c.dates}
                    </div>

                    {/* Course name */}
                    <span
                      style={{
                        fontSize: "0.9rem",
                        fontWeight: 600,
                        color: "#111827",
                        lineHeight: 1.4,
                      }}
                    >
                      {c.title}
                    </span>

                    {/* Enquire link */}
                    <button
                      onClick={() => {
                        const match = STATIC_TRAININGS.find(
                          t => t.attributes.title === c.title
                        );
                        onEnquire(match || null);
                      }}
                      style={{
                        marginLeft: "auto",
                        background: "none",
                        border: "1px solid #009EDC",
                        color: "#009EDC",
                        borderRadius: 6,
                        padding: "6px 14px",
                        fontSize: "0.78rem",
                        fontWeight: 600,
                        cursor: "pointer",
                        flexShrink: 0,
                        whiteSpace: "nowrap",
                      }}
                    >
                      Enquire
                    </button>
                  </div>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Coming Soon — Jul to Oct */}
        <p
          style={{
            fontSize: "0.72rem",
            color: "#9ca3af",
            fontWeight: 700,
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            marginBottom: 20,
          }}
        >
          Coming Soon — Q3 &amp; Q4 2026
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))",
            gap: 20,
          }}
        >
          {COMING_SOON_MONTHS.map(m => (
            <div
              key={m.shortMonth}
              style={{
                background: "#f8f9fc",
                borderRadius: 14,
                padding: "24px 22px",
                border: "1px dashed #d1d5db",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                <div
                  style={{
                    background: "#e5e7eb",
                    color: "#6B7280",
                    borderRadius: 8,
                    padding: "5px 12px",
                    fontSize: "0.73rem",
                    fontWeight: 800,
                    letterSpacing: "0.06em",
                  }}
                >
                  {m.shortMonth}
                </div>
                <span
                  style={{
                    background: "#fef3c7",
                    color: "#92400e",
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    padding: "3px 8px",
                    borderRadius: 20,
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  }}
                >
                  Coming Soon
                </span>
              </div>

              <ul style={{ listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 8 }}>
                {m.courses.map((course, i) => (
                  <li
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 8,
                      fontSize: "0.82rem",
                      color: "#6B7280",
                      lineHeight: 1.45,
                    }}
                  >
                    <i
                      className="bi bi-circle-fill"
                      style={{ fontSize: "0.35rem", color: "#d1d5db", marginTop: 6, flexShrink: 0 }}
                    />
                    {course}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
}


/* ── Main Page ────────────────────────────────────────────────── */
export default function LabServices() {
  const [trainings] = useState(STATIC_TRAININGS);
  const [activeCategory, setActiveCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [enquiryCourse, setEnquiryCourse] = useState(undefined);

  const filtered = trainings.filter(item => {
    const attr = item.attributes;
    const matchesCategory =
      activeCategory === "All" || attr?.vendor === activeCategory;
    const matchesSearch =
      !search ||
      attr?.title?.toLowerCase().includes(search.toLowerCase()) ||
      attr?.vendor?.toLowerCase().includes(search.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <>
      {/* ── HERO ──────────────────────────────────────────── */}
      <section
        style={{
          backgroundImage: `linear-gradient(rgba(5,11,36,0.72), rgba(5,11,36,0.68)), url(${gallery4})`,
          backgroundSize: "cover",
          backgroundPosition: "top center",
          color: "#fff",
          padding: "100px 0 80px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div className="container" style={{ position: "relative", zIndex: 1 }}>
          <div style={{ display: "flex", gap: 40, alignItems: "flex-start", flexWrap: "wrap" }}>

            {/* Left: info card */}
            <div
              style={{
                flex: "1 1 420px",
                backgroundColor: "rgba(255,255,255,0.97)",
                borderRadius: 12,
                padding: "36px 40px 32px",
                maxWidth: 640,
                boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
              }}
            >
              <p
                style={{
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  color: "#009EDC",
                  textTransform: "uppercase",
                  marginBottom: 14,
                }}
              >
                ECL Training &amp; Certifications
              </p>

              <h1
                style={{
                  fontSize: "clamp(2.1rem, 4vw, 3.2rem)",
                  fontWeight: 700,
                  lineHeight: 1.15,
                  marginBottom: 18,
                  color: "#0a1a3a",
                }}
              >
                Build the skills that drive
                <span style={{ color: "#009EDC" }}> real results</span>
              </h1>

              <p
                style={{
                  color: "#4B5563",
                  fontSize: "1.05rem",
                  marginBottom: 28,
                  lineHeight: 1.7,
                }}
              >
                Authorised training in Cisco, Microsoft, Azure, Red Hat and
                Cybersecurity — instructor-led, online or blended delivery.
                Upskill your team or earn your certification.
              </p>

              {/* Search */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  background: "#fff",
                  borderRadius: 10,
                  boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
                  overflow: "hidden",
                }}
              >
                <div style={{ paddingLeft: 16, color: "#9ca3af" }}>
                  <i className="bi bi-search" style={{ fontSize: "1rem" }} />
                </div>
                <input
                  type="text"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search courses, certifications, vendors…"
                  style={{
                    flex: 1,
                    border: "none",
                    outline: "none",
                    padding: "15px 12px",
                    fontSize: "0.93rem",
                    color: "#0a1a3a",
                    background: "transparent",
                  }}
                />
                {search && (
                  <button
                    onClick={() => setSearch("")}
                    style={{
                      background: "none",
                      border: "none",
                      paddingRight: 14,
                      color: "#9ca3af",
                      cursor: "pointer",
                    }}
                  >
                    <i className="bi bi-x-lg" />
                  </button>
                )}
              </div>

              <div style={{ marginTop: 30 }}>
                <div
                  style={{
                    padding: "24px",
                    borderRadius: 8,
                    boxShadow: "0 10px 30px rgba(0,0,0,0.08)",
                    background: "#009EDC",
                  }}
                >
                  <p
                    style={{
                      fontSize: "0.72rem",
                      color: "#fff",
                      fontWeight: 700,
                      letterSpacing: "0.1em",
                      textTransform: "uppercase",
                      marginBottom: 8,
                    }}
                  >
                    Exam Registration &amp; Certification Delivery
                  </p>
                  <p
                    style={{
                      fontSize: "clamp(0.25rem, 1.5vw, 1.8rem)",
                      fontWeight: 700,
                      color: "#0a1a3a",
                      marginBottom: 14,
                    }}
                  >
                    Pearson VUE Accredited Exam Center.
                  </p>
                  <p style={{ color: "#fff", fontSize: "0.8rem", lineHeight: 1.4 }}>
                    Complete your training and book your certification exam at our center.
                    We are fully authorised to host Pearson VUE and Prometric exams so
                    you don't need a separate exam venue.
                    <br />
                    <strong>Register immediately after training</strong> and take your
                    test with confidence on our site.
                  </p>
                  <div style={{ textAlign: "left", marginTop: 16 }}>
                    <a
                      href="#"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        background: "#0a1a3a",
                        color: "#fff",
                        padding: "10px 18px",
                        borderRadius: 8,
                        fontWeight: 700,
                        textDecoration: "none",
                      }}
                      onClick={e => {
                        e.preventDefault();
                        setEnquiryCourse(null);
                      }}
                    >
                      Register for exam booking
                      <i className="bi bi-arrow-right" />
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: flier */}
            <div
              style={{
                flex: "0 1 420px",
                borderRadius: 12,
                overflow: "hidden",
                boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                alignSelf: "center",
              }}
            >
              <img
                src={promptClassFlier}
                alt="Prompt Engineering Class"
                style={{ width: "100%", display: "block" }}
              />
            </div>

          </div>
        </div>
      </section>


      {/* ── TRAINING SCHEDULE ─────────────────────────────── */}
      <ScheduleSection onEnquire={setEnquiryCourse} />


      {/* ── FILTER + COURSES ──────────────────────────────── */}
      <section style={{ padding: "60px 0 80px", background: "#f8f9fc" }}>
        <div className="container">

          <p
            style={{
              fontSize: "0.72rem",
              color: "#009EDC",
              fontWeight: 700,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              marginBottom: 8,
            }}
          >
            Browse Courses
          </p>
          <h2 style={{ fontWeight: 700, color: "#0a1a3a", marginBottom: 32, fontSize: "clamp(1.4rem, 2.5vw, 1.9rem)" }}>
            All training courses
          </h2>

          {/* Filter pills */}
          <div
            style={{
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              marginBottom: 32,
              alignItems: "center",
            }}
          >
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                style={{
                  padding: "8px 20px",
                  borderRadius: 50,
                  border: "2px solid",
                  borderColor: activeCategory === cat ? "#0a1a3a" : "#d1d5db",
                  background: activeCategory === cat ? "#0a1a3a" : "#fff",
                  color: activeCategory === cat ? "#fff" : "#374151",
                  fontWeight: 600,
                  fontSize: "0.875rem",
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
              >
                {cat}
              </button>
            ))}

            {(search || activeCategory !== "All") && (
              <button
                onClick={() => { setSearch(""); setActiveCategory("All"); }}
                style={{
                  padding: "7px 14px",
                  borderRadius: 50,
                  border: "none",
                  background: "#e5e7eb",
                  color: "#6B7280",
                  fontWeight: 500,
                  fontSize: "0.82rem",
                  cursor: "pointer",
                }}
              >
                Clear filters
              </button>
            )}
          </div>

          {/* Results count */}
          <p style={{ color: "#9ca3af", fontSize: "0.85rem", marginBottom: 24 }}>
            {filtered.length === 0
              ? "No courses found"
              : `${filtered.length} course${filtered.length !== 1 ? "s" : ""} found`}
          </p>

          {/* Grid */}
          {filtered.length === 0 ? (
            <EmptyState
              title="No courses found"
              description="Try adjusting your search or category filter. New courses are added regularly."
            />
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(290px, 1fr))",
                gap: 28,
              }}
            >
              {filtered.map(item => (
                <TrainingCard
                  key={item.id}
                  item={item}
                  onEnquire={setEnquiryCourse}
                />
              ))}
            </div>
          )}
        </div>
      </section>


      {/* ── CERTIFICATION TRACKS ──────────────────────────── */}
      <section style={{ background: "#050b24", padding: "80px 0", color: "#fff" }}>
        <div className="container">
          <p
            style={{
              fontSize: "0.72rem",
              color: "#009EDC",
              fontWeight: 700,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              marginBottom: 8,
            }}
          >
            Certification paths
          </p>
          <h2 style={{ fontWeight: 700, color: "#fff", marginBottom: 10 }}>
            Find your track
          </h2>
          <p
            style={{
              color: "rgba(255,255,255,0.58)",
              maxWidth: 480,
              marginBottom: 52,
              lineHeight: 1.7,
            }}
          >
            From entry level to expert — follow a structured path to a
            globally recognised certification.
          </p>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))",
              gap: 24,
            }}
          >
            {CERT_TRACKS.map(track => (
              <div
                key={track.vendor}
                style={{
                  background: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.09)",
                  borderRadius: 14,
                  padding: 28,
                }}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 10,
                    background: track.color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginBottom: 16,
                  }}
                >
                  <i
                    className={`bi ${track.icon}`}
                    style={{ color: "#fff", fontSize: "1.2rem" }}
                  />
                </div>

                <h5 style={{ color: "#fff", fontWeight: 700, marginBottom: 18 }}>
                  {track.vendor}
                </h5>

                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {track.levels.map((level, i) => (
                    <div key={level} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div
                        style={{
                          width: 22,
                          height: 22,
                          borderRadius: "50%",
                          background: i === 0 ? track.color : "transparent",
                          border: `2px solid ${track.color}`,
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <span style={{ fontSize: "0.58rem", color: "#fff", fontWeight: 700 }}>
                          {i + 1}
                        </span>
                      </div>
                      <span
                        style={{
                          fontSize: "0.83rem",
                          color: i === 0 ? "#fff" : "rgba(255,255,255,0.55)",
                          lineHeight: 1.3,
                        }}
                      >
                        {level}
                      </span>
                    </div>
                  ))}
                </div>

                <button
                  onClick={() => {
                    setActiveCategory(track.vendor);
                    window.scrollTo({ top: 580, behavior: "smooth" });
                  }}
                  style={{
                    marginTop: 22,
                    background: "transparent",
                    border: `1px solid ${track.color}`,
                    color: track.color,
                    borderRadius: 6,
                    padding: "8px 0",
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    cursor: "pointer",
                    width: "100%",
                    transition: "background 0.2s",
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = track.color;
                    e.currentTarget.style.color = "#fff";
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.color = track.color;
                  }}
                >
                  View {track.vendor} courses
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* ── PARTNER LOGOS CAROUSEL ────────────────────────── */}
      <section style={{ background: "#fff", padding: "52px 0" }}>
        <div className="container">
          <p
            style={{
              textAlign: "center",
              color: "#d1d5db",
              fontSize: "0.75rem",
              fontWeight: 700,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              marginBottom: 32,
            }}
          >
            Authorised training partner for
          </p>
        </div>

        <div className="carousel-wrapper">
          <div className="carousel-track">
            {CAROUSEL_PARTNERS.map((p, i) => (
              <div
                key={i}
                style={{
                  minWidth: 180,
                  height: 60,
                  background: "#fff",
                  borderRadius: 10,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "0 24px",
                  flexShrink: 0,
                }}
              >
                {p.img ? (
                  <img src={p.img} alt={p.name} style={{ maxWidth: "100%", maxHeight: "100%" }} />
                ) : (
                  <span style={{ fontWeight: 800, fontSize: "0.95rem", color: p.color }}>
                    {p.name}
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* ── CTA BANNER ────────────────────────────────────── */}
      <section style={{ background: "#009EDC", padding: "68px 0" }}>
        <div className="container" style={{ textAlign: "center" }}>
          <h2
            style={{
              color: "#fff",
              fontWeight: 700,
              marginBottom: 12,
              fontSize: "clamp(1.6rem, 3vw, 2.2rem)",
            }}
          >
            Ready to upskill your team?
          </h2>
          <p
            style={{
              color: "rgba(255,255,255,0.85)",
              marginBottom: 32,
              fontSize: "1.05rem",
              maxWidth: 460,
              margin: "0 auto 32px",
            }}
          >
            Speak to our training advisors and design a programme tailored to
            your organisation's goals.
          </p>
          <button
            className="btn btn-lg"
            onClick={() => setEnquiryCourse(null)}
            style={{
              background: "#0a1a3a",
              color: "#fff",
              fontWeight: 700,
              padding: "14px 40px",
              borderRadius: 8,
              fontSize: "0.95rem",
            }}
          >
            Talk to a training advisor
          </button>
        </div>
      </section>


      {/* ── ENQUIRY MODAL ─────────────────────────────────── */}
      {enquiryCourse !== undefined && (
        <EnquiryModal
          course={enquiryCourse}
          onClose={() => setEnquiryCourse(undefined)}
        />
      )}
    </>
  );
}
